'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import DealerCard from '@/components/website/Dealers/DealerCard';
import WebsiteButton from '@/components/website/WebsiteButton';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';
import { CONTACT_PATH, TRADE_PATH } from '@/consts/routes';

export default function DealersPage({ dealerRegions }) {
  return (
    <WebsiteInteriorScrollLayout>
      <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 4 } }}>
        <Container maxWidth="lg">
          <Typography
            component="nav"
            aria-label="Breadcrumb"
            sx={{ mb: 2, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            <Box
              component={Link}
              href={TRADE_PATH}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'text.primary' },
              }}
            >
              Trade
            </Box>
          </Typography>
          <Typography component="h1" variant="h3" sx={{ fontWeight: 500, mb: 2 }}>
            Dealers
          </Typography>
        </Container>
      </Box>

      {dealerRegions.map(({ country, regions }) => (
        <Box key={country} component="section" sx={{ pb: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Typography component="h2" variant="h4" sx={{ fontWeight: 500, mb: { xs: 3, md: 4 } }}>
              {country}
            </Typography>

            {regions.map(region => (
              <Box key={`${country}-${region.name}`} sx={{ mb: { xs: 4, md: 5 } }}>
                <Typography
                  component="h3"
                  variant="h5"
                  sx={{ fontWeight: 500, mb: { xs: 2.5, md: 3 } }}
                >
                  {region.name}
                </Typography>

                <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                  {region.locations.flatMap(location =>
                    location.dealers.map(dealer => (
                      <Grid
                        key={`${dealer.id ?? dealer.name}-${location.city || region.name}`}
                        item
                        xs={12}
                        sm={6}
                        lg={4}
                      >
                        <DealerCard dealer={dealer} city={location.city} />
                      </Grid>
                    )),
                  )}
                </Grid>
              </Box>
            ))}
          </Container>
        </Box>
      ))}

      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h2" variant="h4" sx={{ fontWeight: 500, mb: 1 }}>
            Talk with NIICO
          </Typography>
          <Typography sx={{ lineHeight: 1.7, mb: 3 }}>
            Please feel free to reach out regarding any upcoming projects with NIICO.
          </Typography>
          <WebsiteButton href={CONTACT_PATH}>Contact Us</WebsiteButton>
        </Container>
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

DealersPage.propTypes = {
  dealerRegions: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      regions: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          locations: PropTypes.arrayOf(
            PropTypes.shape({
              city: PropTypes.string,
              dealers: PropTypes.arrayOf(PropTypes.object).isRequired,
            }),
          ).isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
};
